import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Mail, Users, Send, CheckCircle2, AlertCircle, Loader2, X, Plus, Archive, UserPlus, Shield, ArrowLeft } from 'lucide-react';
import { apiClient } from '@dukene/shared';
import { useNavigate } from 'react-router-dom';

interface RecipientCounts {
    merchants: number;
    affiliates: number;
    customers: number;
    suppliers: number;
}

interface SendEmailData {
    subject: string;
    body: string;
    roles: string[];
    individualEmails: string[];
    fromCategory: string;
}

export default function ComposeEmail() {
    const navigate = useNavigate();
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [fromCategory, setFromCategory] = useState('general');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [individualEmail, setIndividualEmail] = useState('');
    const [individualEmailsList, setIndividualEmailsList] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Fetch recipient counts
    const { data: counts, isLoading: loadingCounts } = useQuery<RecipientCounts>({
        queryKey: ['email-recipients'],
        queryFn: async () => {
            return apiClient.get<RecipientCounts>('/admin/email/recipients');
        },
    });

    // Send email mutation
    const sendMutation = useMutation({
        mutationFn: (data: SendEmailData) => {
            return apiClient.post('/admin/email/send', data);
        },
        onSuccess: (response: any) => {
            setSuccessMessage(response.message || 'Emails sent successfully!');
            setSubject('');
            setBody('');
            setSelectedRoles([]);
            setIndividualEmailsList([]);
            setErrorMessage(null);
            setTimeout(() => {
                navigate('/email');
            }, 2000);
        },
        onError: (error: any) => {
            setErrorMessage(error.response?.data?.error || 'Failed to send emails. Please try again.');
            setSuccessMessage(null);
        },
    });

    const handleRoleToggle = (role: string) => {
        setSelectedRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const addIndividualEmail = () => {
        if (individualEmail && individualEmail.includes('@') && !individualEmailsList.includes(individualEmail)) {
            setIndividualEmailsList(prev => [...prev, individualEmail.trim().toLowerCase()]);
            setIndividualEmail('');
        }
    };

    const removeIndividualEmail = (email: string) => {
        setIndividualEmailsList(prev => prev.filter(e => e !== email));
    };

    const handleSend = () => {
        if (!subject.trim() || !body.trim()) {
            setErrorMessage('Please provide both subject and message body.');
            return;
        }

        if (selectedRoles.length === 0 && individualEmailsList.length === 0) {
            setErrorMessage('Please select at least one role or add an individual email.');
            return;
        }

        sendMutation.mutate({
            subject,
            body,
            roles: selectedRoles,
            individualEmails: individualEmailsList,
            fromCategory,
        });
    };

    const calculateTotalRecipients = () => {
        let total = individualEmailsList.length;
        if (counts) {
            if (selectedRoles.includes('merchants')) total += counts.merchants;
            if (selectedRoles.includes('affiliates')) total += counts.affiliates;
            if (selectedRoles.includes('customers')) total += counts.customers;
            if (selectedRoles.includes('suppliers')) total += counts.suppliers;
        }
        return total;
    };

    const senderProfiles = [
        { id: 'general', label: 'General Info', email: 'info@dukene.co.ke' },
        { id: 'onboarding', label: 'Onboarding', email: 'onboarding@dukene.co.ke' },
        { id: 'payments', label: 'Payments', email: 'payments@dukene.co.ke' },
        { id: 'orders', label: 'Orders', email: 'orders@dukene.co.ke' },
        { id: 'support', label: 'Support', email: 'support@dukene.co.ke' },
    ];

    return (
        <div className="max-w-5xl mx-auto space-y-8 pb-12">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <button
                        onClick={() => navigate('/email')}
                        className="flex items-center gap-2 text-gray-500 hover:text-black mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Emails
                    </button>
                    <h1 className="text-4xl font-black tracking-tight text-black">Compose Email</h1>
                    <p className="text-gray-500 mt-2">Send bulk emails to platform users and partners</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Mail className="w-5 h-5" />
                            Message Content
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Subject</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="e.g. Scheduled System Maintenance"
                                    className="w-full h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Message Body</label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Write your announcement here..."
                                    rows={12}
                                    className="w-full px-4 py-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:bg-white focus:outline-none transition-all resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Individual Emails Section */}
                    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Specific Recipients
                        </h2>

                        <div className="flex gap-2 mb-4">
                            <input
                                type="email"
                                value={individualEmail}
                                onChange={(e) => setIndividualEmail(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && addIndividualEmail()}
                                placeholder="Enter email address"
                                className="flex-1 h-12 px-4 rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-black focus:outline-none transition-all"
                            />
                            <button
                                onClick={addIndividualEmail}
                                className="h-12 px-6 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                            >
                                Add
                            </button>
                        </div>

                        {individualEmailsList.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {individualEmailsList.map(email => (
                                    <div key={email} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm font-medium border border-gray-200">
                                        {email}
                                        <button onClick={() => removeIndividualEmail(email)} className="text-gray-400 hover:text-black">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar Controls */}
                <div className="space-y-6">
                    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Sender Profile
                        </h2>
                        <div className="space-y-2">
                            {senderProfiles.map((profile) => (
                                <button
                                    key={profile.id}
                                    onClick={() => setFromCategory(profile.id)}
                                    className={`w-full flex flex-col items-start p-3 rounded-xl border-2 transition-all ${fromCategory === profile.id
                                        ? 'border-black bg-black text-white'
                                        : 'border-gray-50 bg-gray-50 hover:border-gray-200 text-gray-700'
                                        }`}
                                >
                                    <span className="text-xs font-black uppercase tracking-wider">{profile.label}</span>
                                    <span className={`text-[10px] ${fromCategory === profile.id ? 'text-gray-400' : 'text-gray-500'}`}>{profile.email}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Target Groups
                        </h2>

                        <div className="space-y-3">
                            {[
                                { id: 'merchants', label: 'Merchants & Owners', icon: Users, count: counts?.merchants },
                                { id: 'suppliers', label: 'Suppliers', icon: Archive, count: counts?.suppliers },
                                { id: 'affiliates', label: 'Affiliates', icon: UserPlus, count: counts?.affiliates },
                                { id: 'customers', label: 'Active Customers', icon: Users, count: counts?.customers },
                            ].map(role => (
                                <label
                                    key={role.id}
                                    className={`group flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedRoles.includes(role.id)
                                        ? 'border-black bg-black text-white'
                                        : 'border-gray-50 bg-gray-50 hover:border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={selectedRoles.includes(role.id)}
                                            onChange={() => handleRoleToggle(role.id)}
                                        />
                                        <span className="font-bold text-sm tracking-tight">{role.label}</span>
                                    </div>
                                    <span className={`text-xs font-black ${selectedRoles.includes(role.id) ? 'text-gray-400' : 'text-gray-400'}`}>
                                        {loadingCounts ? '...' : role.count || 0}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div className="mt-8 pt-8 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Total Reach</span>
                                <span className="text-2xl font-black text-black">{calculateTotalRecipients()}</span>
                            </div>

                            <button
                                onClick={handleSend}
                                disabled={sendMutation.isPending}
                                className="w-full h-14 bg-black text-white rounded-2xl font-black flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 transition-all shadow-xl shadow-black/10"
                            >
                                {sendMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5" />
                                        Send Broadcast
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Status Notifications */}
                    {successMessage && (
                        <div className="bg-green-50 border border-green-100 rounded-2xl p-6 flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                            <div>
                                <h3 className="font-bold text-green-900">Success</h3>
                                <p className="text-sm text-green-700 mt-1">{successMessage}</p>
                            </div>
                        </div>
                    )}

                    {errorMessage && (
                        <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex items-start gap-4">
                            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                            <div>
                                <h3 className="font-bold text-red-900">Error</h3>
                                <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
